import React from "react";

import { reportClientError } from "../core/errorReporter.js";

// Auto-recovery tuning.
// 1. On the first crash we retry the subtree in place (a fresh mount fixes most
//    transient render races without a jarring full reload).
// 2. If it crashes again immediately, we do ONE automatic full page reload.
// 3. A sessionStorage counter caps automatic reloads so a genuinely-broken page
//    can never reload-loop forever — after the cap we fall back to the manual UI.
const RETRY_KEY = "rc_eb_auto_reloads";
const MAX_AUTO_RELOADS = 2;
// If the page has survived this long since the last auto-reload, treat the crash
// as new (not part of a loop) and reset the counter.
const RELOAD_RESET_MS = 15000;
const LAST_RELOAD_KEY = "rc_eb_last_reload_at";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, exhausted: false };
    this._retried = false;
  }

  static getDerivedStateFromError(error) {
    return { error: error };
  }

  componentDidMount() {
    // The page mounted cleanly. If enough time has passed since the last
    // automatic reload, the previous crash wasn't a loop — clear the counter.
    try {
      const last = Number(sessionStorage.getItem(LAST_RELOAD_KEY) || 0);
      if (last && Date.now() - last > RELOAD_RESET_MS) {
        sessionStorage.removeItem(RETRY_KEY);
        sessionStorage.removeItem(LAST_RELOAD_KEY);
      }
    } catch (_) {
      /* sessionStorage unavailable — ignore */
    }
  }

  componentDidCatch(error, errorInfo) {
    reportClientError({
      type: "react_error_boundary",
      message: (error && error.message) || "React render error",
      context: {
        stack: (error && error.stack) || null,
        componentStack: (errorInfo && errorInfo.componentStack) || null,
      },
    });

    // Step 1: first crash in this mount → retry the subtree in place.
    if (!this._retried) {
      this._retried = true;
      // Defer so React finishes committing the fallback before we reset.
      setTimeout(() => this.setState({ error: null }), 0);
      return;
    }

    // Step 2: the retry also crashed → one guarded automatic full reload.
    let reloads = 0;
    try {
      reloads = Number(sessionStorage.getItem(RETRY_KEY) || 0);
    } catch (_) {
      /* ignore */
    }

    if (reloads < MAX_AUTO_RELOADS) {
      try {
        sessionStorage.setItem(RETRY_KEY, String(reloads + 1));
        sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
      } catch (_) {
        /* ignore */
      }
      // Small delay lets the error report fire before we tear the page down.
      setTimeout(() => location.reload(), 400);
      return;
    }

    // Step 3: auto-recovery exhausted → show the manual fallback.
    this.setState({ exhausted: true });
  }

  render() {
    if (this.state.error) {
      // Auto-recovery in progress (retry queued or reload pending): render a
      // quiet placeholder rather than the alarming manual fallback.
      if (!this.state.exhausted) {
        return (
          <div
            role="status"
            aria-live="polite"
            style={{
              minHeight: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
            }}
          >
            <p style={{ opacity: 0.6 }}>Recovering…</p>
          </div>
        );
      }

      // Only reached after automatic recovery has been exhausted.
      return (
        <div
          role="alert"
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            padding: "24px",
            textAlign: "center",
          }}
        >
          <h2>Something went wrong</h2>
          <p>This page hit an unexpected error. Try reloading.</p>
          <button
            type="button"
            onClick={() => {
              try {
                sessionStorage.removeItem(RETRY_KEY);
                sessionStorage.removeItem(LAST_RELOAD_KEY);
              } catch (_) {
                /* ignore */
              }
              location.reload();
            }}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
